// FLEXX Academy — premium dark luxury Art Deco quiz result panel.
// Shared by all 5 course quiz modals (Beginner, Advanced, CFD, Commodities, v5).
// Matches FlexxCertificate aesthetic: gold Art Deco frame, Cinzel typography,
// SVG trophy/study icon (no emoji), serif body text.
//
// Prop API:
//   passed            boolean
//   score             number
//   total             number
//   moduleTitle       string (course/module name for cert)
//   tierLabel         string (tier label passed to generateCert)
//   studentName       string
//   setStudentName    (value) => void
//   onGenerateCert    () => void   (caller handles cert generation + teardown)
//   onTryAgain        () => void
//   onClose           () => void
//   passFeedback?     string  (custom success tagline)
//   failFeedback?     string  (custom fail tagline)

import React, { useEffect } from "react";

const GOLD = "#C9A227";
const GOLD_LIGHT = "#E8C84A";
const GOLD_DARK = "#8B6914";
const RED = "#B23A48";
const RED_LIGHT = "#D46A76";

const FONTS_HREF =
  "https://fonts.googleapis.com/css2?family=Cinzel:wght@400;500;600;700;900&family=Cormorant+Garamond:ital,wght@0,400;0,500;0,600;1,400&display=swap";

function useArtDecoFonts() {
  useEffect(() => {
    if (typeof document === "undefined") return;
    if (document.getElementById("flexx-quiz-fonts")) return;
    const l = document.createElement("link");
    l.id = "flexx-quiz-fonts";
    l.rel = "stylesheet";
    l.href = FONTS_HREF;
    document.head.appendChild(l);
  }, []);
}

function LaurelTrophy({ color = GOLD }) {
  return (
    <svg width="78" height="78" viewBox="0 0 100 100" style={{ filter: `drop-shadow(0 0 12px ${color}55)` }}>
      <defs>
        <linearGradient id="cup-grad" x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor={GOLD_LIGHT} />
          <stop offset="60%" stopColor={color} />
          <stop offset="100%" stopColor={GOLD_DARK} />
        </linearGradient>
      </defs>
      {/* Left laurel */}
      <g stroke={color} strokeWidth="1.2" fill="none" opacity="0.85">
        <path d="M 20 50 Q 14 38 20 22" />
        <path d="M 19 46 Q 13 44 10 40" />
        <path d="M 20 40 Q 14 38 11 33" />
        <path d="M 21 34 Q 15 32 13 27" />
        <path d="M 22 28 Q 17 26 16 22" />
      </g>
      {/* Right laurel */}
      <g stroke={color} strokeWidth="1.2" fill="none" opacity="0.85">
        <path d="M 80 50 Q 86 38 80 22" />
        <path d="M 81 46 Q 87 44 90 40" />
        <path d="M 80 40 Q 86 38 89 33" />
        <path d="M 79 34 Q 85 32 87 27" />
        <path d="M 78 28 Q 83 26 84 22" />
      </g>
      {/* Trophy cup */}
      <g>
        <path d="M 35 28 L 65 28 L 62 58 Q 50 66 38 58 Z" fill="url(#cup-grad)" stroke={GOLD_DARK} strokeWidth="0.8" />
        <rect x="46" y="66" width="8" height="8" fill={color} />
        <rect x="38" y="74" width="24" height="4" fill={color} />
        {/* Handles */}
        <path d="M 35 32 Q 26 34 26 42 Q 26 48 32 48" stroke={color} strokeWidth="2" fill="none" />
        <path d="M 65 32 Q 74 34 74 42 Q 74 48 68 48" stroke={color} strokeWidth="2" fill="none" />
        {/* Shine */}
        <path d="M 40 32 L 44 32 L 42 50 Z" fill={GOLD_LIGHT} opacity="0.5" />
      </g>
    </svg>
  );
}

function BookIcon({ color = RED_LIGHT }) {
  return (
    <svg width="72" height="72" viewBox="0 0 100 100" style={{ filter: `drop-shadow(0 0 12px ${color}44)` }}>
      <rect x="22" y="22" width="56" height="60" fill="none" stroke={color} strokeWidth="2" />
      <line x1="50" y1="22" x2="50" y2="82" stroke={color} strokeWidth="1.2" opacity="0.6" />
      <line x1="28" y1="32" x2="46" y2="32" stroke={color} strokeWidth="0.8" opacity="0.7" />
      <line x1="28" y1="40" x2="46" y2="40" stroke={color} strokeWidth="0.8" opacity="0.7" />
      <line x1="28" y1="48" x2="46" y2="48" stroke={color} strokeWidth="0.8" opacity="0.7" />
      <line x1="54" y1="32" x2="72" y2="32" stroke={color} strokeWidth="0.8" opacity="0.7" />
      <line x1="54" y1="40" x2="72" y2="40" stroke={color} strokeWidth="0.8" opacity="0.7" />
      <line x1="54" y1="48" x2="72" y2="48" stroke={color} strokeWidth="0.8" opacity="0.7" />
    </svg>
  );
}

function CornerFlourish({ pos }) {
  const common = { position: "absolute", width: 30, height: 30, pointerEvents: "none" };
  const style = { ...common };
  if (pos === "tl") { style.top = 4; style.left = 4; }
  if (pos === "tr") { style.top = 4; style.right = 4; style.transform = "scaleX(-1)"; }
  if (pos === "bl") { style.bottom = 4; style.left = 4; style.transform = "scaleY(-1)"; }
  if (pos === "br") { style.bottom = 4; style.right = 4; style.transform = "scale(-1,-1)"; }
  return (
    <svg style={style} viewBox="0 0 30 30" fill="none">
      <path d="M 2 2 L 14 2 M 2 2 L 2 14" stroke={GOLD} strokeWidth="1" />
      <path d="M 2 4 L 10 4 M 4 2 L 4 10" stroke={GOLD} strokeWidth="0.4" opacity="0.6" />
      <circle cx="2" cy="2" r="1.2" fill={GOLD} />
    </svg>
  );
}

export default function FlexxQuizResultPanel({
  passed,
  score,
  total,
  moduleTitle = "",
  tierLabel = "",
  studentName = "",
  setStudentName = () => {},
  onGenerateCert = () => {},
  onTryAgain = () => {},
  onClose = () => {},
  passFeedback = "Excellent work!",
  failFeedback = "Need 75% to pass. Review the lesson and try again.",
  // Optional affiliate referral capture (Advanced / CFD / Commodities tracks)
  affiliateCode = null,
  setAffiliateCode = null,
  affiliatePlaceholder = "Referral Code (optional — e.g. FLEXX123)",
}) {
  useArtDecoFonts();
  const pct = Math.round((score / Math.max(total, 1)) * 100);
  const accent = passed ? GOLD : RED;
  const accentLight = passed ? GOLD_LIGHT : RED_LIGHT;

  const overlay = {
    position: "fixed", inset: 0, zIndex: 260,
    background: "rgba(0,0,0,0.94)", backdropFilter: "blur(8px)", WebkitBackdropFilter: "blur(8px)",
    display: "flex", alignItems: "center", justifyContent: "center",
    padding: "20px 16px", overflowY: "auto",
  };
  const frame = {
    position: "relative", width: "100%", maxWidth: 560,
    background: "linear-gradient(180deg, #141208 0%, #0a0a0a 60%, #050505 100%)",
    border: `2px solid ${accent}`,
    outline: `1px solid ${passed ? GOLD_DARK : "#5a1f26"}`,
    outlineOffset: 5,
    boxShadow: `0 0 80px ${passed ? "rgba(201,162,39,0.35)" : "rgba(178,58,72,0.30)"}, 0 20px 60px rgba(0,0,0,0.8)`,
    padding: 30,
  };
  const inner = {
    position: "relative",
    border: `1px solid ${accent}55`,
    outline: `1px solid ${accent}22`,
    outlineOffset: 3,
    padding: "34px 26px 26px",
  };

  const inputStyle = {
    width: "100%", padding: "12px 14px",
    background: "rgba(20,18,8,0.6)",
    border: `1px solid ${GOLD}55`,
    borderBottom: `2px solid ${GOLD}`,
    color: "#F4E8C8", fontSize: 15,
    fontFamily: "'Cormorant Garamond', serif", fontStyle: "italic",
    outline: "none", letterSpacing: 1, textAlign: "center",
    marginBottom: 12, borderRadius: 2,
  };
  const primaryBtn = {
    width: "100%", padding: "13px", cursor: "pointer",
    background: `linear-gradient(135deg, ${GOLD_LIGHT} 0%, ${GOLD} 55%, ${GOLD_DARK} 100%)`,
    color: "#0a0a0a", fontSize: 11, letterSpacing: 4,
    fontFamily: "'Cinzel', serif", fontWeight: 700,
    border: `1px solid ${GOLD_DARK}`, borderRadius: 2,
    boxShadow: `0 0 20px ${GOLD}44, 0 4px 12px rgba(0,0,0,0.6)`,
  };
  const ghostBtn = (color) => ({
    flex: 1, padding: "11px", cursor: "pointer",
    background: "transparent", color,
    fontSize: 10, letterSpacing: 3,
    fontFamily: "'Cinzel', serif", fontWeight: 600,
    border: `1px solid ${color}66`, borderRadius: 2,
  });

  return (
    <div data-testid="flexx-quiz-result-overlay" style={overlay}>
      <div data-testid={`flexx-quiz-result-${passed ? "passed" : "failed"}`} style={frame}>
        <CornerFlourish pos="tl" />
        <CornerFlourish pos="tr" />
        <CornerFlourish pos="bl" />
        <CornerFlourish pos="br" />

        <div style={inner}>
          {/* Icon */}
          <div style={{ display: "flex", justifyContent: "center", marginBottom: 14 }}>
            {passed ? <LaurelTrophy color={accent} /> : <BookIcon color={accentLight} />}
          </div>

          {/* Title */}
          <div style={{
            textAlign: "center",
            fontFamily: "'Cinzel', serif", fontWeight: 700,
            fontSize: 28, letterSpacing: 8,
            color: accentLight,
            textShadow: `0 0 18px ${accent}66, 0 0 2px ${accent}`,
            marginBottom: 4,
          }}>
            {passed ? "PASSED" : "NOT QUITE"}
          </div>

          {/* Divider */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 10, marginBottom: 18 }}>
            <div style={{ width: 70, height: 1, background: `linear-gradient(90deg, transparent, ${accent}aa)` }} />
            <div style={{ width: 5, height: 5, background: accent, transform: "rotate(45deg)" }} />
            <div style={{ width: 70, height: 1, background: `linear-gradient(90deg, ${accent}aa, transparent)` }} />
          </div>

          {/* Score */}
          <div style={{
            textAlign: "center", fontFamily: "'Cormorant Garamond', serif",
            fontSize: 15, color: "#cfc5a8", marginBottom: 8, letterSpacing: 1,
          }}>
            You scored
          </div>
          <div style={{
            textAlign: "center", fontFamily: "'Cinzel', serif", fontWeight: 700,
            fontSize: 36, color: accent, letterSpacing: 4, marginBottom: 4,
          }}>
            {score} <span style={{ color: `${accent}88`, fontWeight: 400 }}>/</span> {total}
          </div>
          <div style={{
            textAlign: "center", fontFamily: "'Cinzel', serif",
            fontSize: 11, color: `${accent}bb`, letterSpacing: 4, marginBottom: 18,
          }}>
            {pct}%
          </div>

          {/* Feedback */}
          <div style={{
            textAlign: "center", fontFamily: "'Cormorant Garamond', serif", fontStyle: "italic",
            fontSize: 14, color: "#a9a089", marginBottom: 22, lineHeight: 1.55, padding: "0 8px",
          }}>
            {passed ? passFeedback : failFeedback}
          </div>

          {/* Score bar (thin gold) */}
          <div style={{
            height: 2, background: "#1a1708", marginBottom: 22, position: "relative",
            boxShadow: `inset 0 0 4px ${accent}22`,
          }}>
            <div style={{
              width: `${pct}%`, height: "100%",
              background: `linear-gradient(90deg, ${accent}88, ${accent}, ${accentLight})`,
              boxShadow: `0 0 10px ${accent}aa`,
              transition: "width 1s ease-out",
            }} />
          </div>

          {/* Certificate unlock */}
          {passed && (
            <div style={{
              padding: "18px 18px 20px",
              background: "rgba(201,162,39,0.06)",
              border: `1px solid ${GOLD}33`,
              outline: `1px solid ${GOLD}11`, outlineOffset: 2,
              marginBottom: 16, position: "relative",
            }}>
              <div style={{
                fontFamily: "'Cinzel', serif", fontSize: 9, fontWeight: 700,
                color: GOLD, letterSpacing: 5, textAlign: "center", marginBottom: 10,
              }}>
                ✦  CERTIFICATE UNLOCKED  ✦
              </div>
              <div style={{
                fontFamily: "'Cormorant Garamond', serif", fontStyle: "italic",
                fontSize: 13, color: "#cfc5a8", textAlign: "center", marginBottom: 14,
              }}>
                Inscribe your name to mint your certificate of completion
              </div>
              <input
                data-testid="flexx-quiz-cert-name-input"
                type="text"
                value={studentName}
                onChange={(e) => setStudentName(e.target.value)}
                placeholder="Your Full Name"
                autoComplete="name"
                autoFocus
                style={inputStyle}
              />
              {setAffiliateCode && (
                <input
                  data-testid="flexx-quiz-affiliate-input"
                  type="text"
                  value={affiliateCode || ""}
                  onChange={(e) => setAffiliateCode(e.target.value)}
                  placeholder={affiliatePlaceholder}
                  style={{ ...inputStyle, fontSize: 12, marginBottom: 14 }}
                />
              )}
              <button
                data-testid="flexx-quiz-generate-cert-btn"
                onClick={onGenerateCert}
                disabled={!studentName || !studentName.trim()}
                style={{
                  ...primaryBtn,
                  opacity: !studentName || !studentName.trim() ? 0.5 : 1,
                  cursor: !studentName || !studentName.trim() ? "not-allowed" : "pointer",
                }}
              >
                GENERATE MY CERTIFICATE
              </button>
            </div>
          )}

          {/* Actions */}
          <div style={{ display: "flex", gap: 10 }}>
            {!passed && (
              <button
                data-testid="flexx-quiz-try-again-btn"
                onClick={onTryAgain}
                style={ghostBtn(accent)}
              >
                TRY AGAIN
              </button>
            )}
            <button
              data-testid="flexx-quiz-close-btn"
              onClick={onClose}
              style={ghostBtn(passed ? GOLD_DARK : "#6e6e6e")}
            >
              {passed ? "CLOSE" : "BACK TO LESSON"}
            </button>
          </div>

          {/* Module label */}
          {moduleTitle && (
            <div style={{
              marginTop: 18, textAlign: "center",
              fontFamily: "'Cinzel', serif", fontSize: 8,
              letterSpacing: 4, color: `${GOLD}88`,
            }}>
              {moduleTitle.toUpperCase()}{tierLabel ? ` · ${tierLabel.toUpperCase()}` : ""}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
